import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Country } from 'country-state-city';
import * as dashboardApi from '../../../api/dashboardApi';
import { useTheme } from '../../../context/ThemeContext';
import { LegalModal, type LegalDocumentType } from '../../../components/LegalModal';
import styles from './LandingPage.module.css';

type NavLink = {
  label: string;
  href: string;
  isRoute?: boolean;
};

type CoverageItem = {
  title: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
};

const navLinks: NavLink[] = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', href: '/pricing', isRoute: true },
  { label: 'FAQs', href: '/faq', isRoute: true },
];

const iconProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const coverageItems: CoverageItem[] = [
  {
    title: 'Federal & State Hearings',
    description: 'Realtime capture + speaker detection across 50 states.',
    accent: '#6172f3',
    icon: (
      <svg {...iconProps}>
        <path d="M5 11h14" />
        <path d="M7 7h10" />
        <path d="M9 3h6" />
        <path d="M4 15h16" />
      </svg>
    ),
  },
  {
    title: 'Panel Meetings',
    description: 'Auto-tagged agendas and instant stakeholder summaries.',
    accent: '#f04438',
    icon: (
      <svg {...iconProps}>
        <rect x="4" y="5" width="16" height="6" rx="2" />
        <rect x="4" y="13" width="10" height="6" rx="2" />
        <path d="M16 13h4" />
      </svg>
    ),
  },
  {
    title: 'Federal & State Legislation',
    description: 'Track bills, amendments, and last-minute changes as they happen.',
    accent: '#6172f3',
    icon: (
      <svg {...iconProps}>
        <path d="M8 4h8v16H8z" />
        <path d="M8 9h8" />
        <path d="M12 4v5" />
      </svg>
    ),
  },
  {
    title: 'Committee & Agency Reports',
    description: 'Digest dense releases into one-page takeaways.',
    accent: '#12b76a',
    icon: (
      <svg {...iconProps}>
        <path d="M5 6h14" />
        <path d="M5 12h14" />
        <path d="M5 18h9" />
      </svg>
    ),
  },
  {
    title: 'Congressional Sentiment',
    description: 'Gauge tone across speeches and pressers.',
    accent: '#444ce7',
    icon: (
      <svg {...iconProps}>
        <path d="M4 16l4-4 4 3 4-6 4 3" />
        <path d="M4 19h16" />
      </svg>
    ),
  },
  {
    title: 'News & Media',
    description: 'Surface trusted coverage and cite original articles.',
    accent: '#6172f3',
    icon: (
      <svg {...iconProps}>
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M8 9h8" />
        <path d="M8 13h5" />
      </svg>
    ),
  },
  {
    title: 'Local Ordinances',
    description: 'Never miss municipal hearings or city council votes.',
    accent: '#6172f3',
    icon: (
      <svg {...iconProps}>
        <path d="M12 21s6-4.35 6-9a6 6 0 10-12 0c0 4.65 6 9 6 9z" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
  },
  {
    title: 'Proposed & Final Rules',
    description: 'Monitor comment windows and compliance triggers.',
    accent: '#444ce7',
    icon: (
      <svg {...iconProps}>
        <path d="M7 7h10" />
        <path d="M5 17h14" />
        <path d="M12 17V7" />
      </svg>
    ),
  },

];

type LiveStats = {
  regulations: number;
  jurisdictions: number;
  countries: number;
};

const dashboardSlides = [
  { src: '/Chart.png',      label: 'Regulation Trends & Analytics' },
  { src: '/Regulations.png',label: 'All Regulations View' },
  { src: '/Newsletters.png',label: 'Policy Newsletters' },
  { src: '/GlobalMap.png',  label: 'Global Coverage Map' },
];

const chatSlides = [
  { src: '/Chat.png', label: 'AI Policy Assistant' },
  { src: '/GreetingsChat.png', label: 'Natural Conversations' },
  { src: '/TextChat.png', label: 'Detailed Analysis' },
  { src: '/SourceChat.png', label: 'Source Citations' },
];

const searchSlides = [
  { src: '/SearchBar.png',      label: 'Smart Search Interface' },
  { src: '/FiltersSearch.png', label: 'Advanced Search Filters' },
  { src: '/Results.png',       label: 'Search Results Preview' },
];

const heroFeatures = [
  {
    label: 'Federal & State Hearings Tracking',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18"/><path d="M5 21V10l7-7 7 7v11"/><path d="M9 21v-6h6v6"/>
      </svg>
    ),
  },
  {
    label: 'Real-time Bill Tracking',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/>
      </svg>
    ),
  },
  {
    label: 'AI-powered Summaries',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/>
      </svg>
    ),
  },
  {
    label: 'Jurisdiction Comparison',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><line x1="12" y1="3" x2="12" y2="21"/>
      </svg>
    ),
  },
];

export const LandingPage: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [jurisdictionFilter, setJurisdictionFilter] = useState('All');
  const [liveStats, setLiveStats] = useState<LiveStats | null>(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [chatSlideIndex, setChatSlideIndex] = useState(0);
  const [searchSlideIndex, setSearchSlideIndex] = useState(0);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalDocumentType, setLegalDocumentType] = useState<LegalDocumentType>('privacy');
  const { theme } = useTheme();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Use the same APIs as the user dashboard
        const [jurisData, statsData] = await Promise.all([
          dashboardApi.getTotalJurisdictions(),
          dashboardApi.getJurisdictionStats({})
        ]);
        
        // Calculate countries the same way as dashboard
        const SUPRA_CODES = new Set(['EU', 'UN', 'OECD', 'GCC', 'ASEAN', 'APEC', 'AU']);
        const countriesCount = Object.keys(statsData?.by_country ?? {})
          .filter(k => !SUPRA_CODES.has(k) && !!Country.getCountryByCode(k))
          .length;
        
        // Calculate total regulations the same way as dashboard  
        const totalRegulations = (statsData?.stats ?? []).reduce((sum, s) => sum + s.regulation_count, 0);
        
        setLiveStats({
          regulations: totalRegulations,
          jurisdictions: jurisData?.total ?? 0,
          countries: countriesCount
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    
    fetchStats();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlideIndex(i => (i + 1) % dashboardSlides.length), 3800);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setChatSlideIndex(i => (i + 1) % chatSlides.length), 3800);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSearchSlideIndex(i => (i + 1) % searchSlides.length), 3800);
    return () => clearInterval(t);
  }, []);
  const logoSrc = '/Policy Ora Final Logo.png';

  const openLegalModal = (documentType: LegalDocumentType) => {
    setLegalDocumentType(documentType);
    setLegalModalOpen(true);
  };

  // Scroll animation observer
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Strip any leftover visible classes from a previous mount (SPA navigation)
    document.querySelectorAll(`.${styles.scrollAnimated}`).forEach((el) => {
      el.classList.remove(styles.visible);
    });

    const timer = setTimeout(() => {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add(styles.visible);
              observerRef.current?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px 0px 0px' }
      );
      document.querySelectorAll(`.${styles.scrollAnimated}`).forEach((el) => {
        observerRef.current?.observe(el);
      });
    }, 80);

    return () => {
      clearTimeout(timer);
      observerRef.current?.disconnect();
    };
  }, []);

  // Counter animation
  const animateCounter = useCallback((element: HTMLElement, target: string) => {
    const numericTarget = parseInt(target.replace(/[^0-9]/g, ''), 10);
    if (!numericTarget) return;
    const duration = 1800;
    const steps = 60;
    const stepValue = numericTarget / steps;
    let current = 0;

    const interval = setInterval(() => {
      current += stepValue;
      if (current >= numericTarget) {
        current = numericTarget;
        clearInterval(interval);
      }
      element.textContent = Math.floor(current).toLocaleString();
    }, duration / steps);
  }, []);

  useEffect(() => {
    const counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !entry.target.classList.contains('counted')) {
            const element = entry.target as HTMLElement;
            const targetValue = element.getAttribute('data-target');
            if (targetValue) {
              element.classList.add('counted');
              animateCounter(element, targetValue);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    const counters = document.querySelectorAll('[data-target]');
    counters.forEach((counter) => counterObserver.observe(counter));

    return () => counterObserver.disconnect();
  }, [animateCounter, liveStats]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const limitSeconds = 8;
    const handleTimeUpdate = () => {
      if (video.currentTime >= limitSeconds) {
        video.currentTime = 0;
        video.play().catch(() => {
          /* ignore autoplay blocking */
        });
      }
    };

    const startPlayback = () => {
      video.muted = true;
      video.play().catch(() => {
        /* autoplay may still be blocked in some browsers */
      });
    };

    if (video.readyState >= 2) {
      startPlayback();
    } else {
      video.addEventListener('loadedmetadata', startPlayback, { once: true });
    }

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', startPlayback);
    };
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.bubbleField} aria-hidden="true" />
      <header className={styles.navBarWrapper}>
        <div className={styles.navBar}>
        <div className={styles.logo}>
          <div className={styles.logoImageWrap}>
            <img
              src={logoSrc}
              alt="Policyora"
              className={`${styles.logoImage} ${theme === 'dark' ? styles.logoImageDark : styles.logoImageLight}`}
            />
          </div>
        </div>
        <nav className={styles.navLinks} aria-label="Primary">
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link key={link.href} to={link.href} className={`${styles.navLink} ${styles.navPill}`}>
                {link.label}
              </Link>
            ) : (
              <a key={link.href} href={link.href} className={`${styles.navLink} ${styles.navPill}`}>
                {link.label}
              </a>
            )
          ))}
        </nav>
        <div className={styles.navActions}>
          <Link to="/login" className={styles.navGhost}>
            Log In
          </Link>
          <Link to="/signup" className={styles.navPrimary}>
            Sign Up
          </Link>
          <div className={styles.themeToggleWrapper}>
          </div>
        </div>
        </div>
      </header>

      <main className={styles.main}>
        <section className={styles.hero}>
          {/* ── Left purple brand panel ── */}
          <div className={styles.heroLeft}>
            <div className={styles.heroLeftContent}>
              <div className={styles.heroLogoRow}>
                <div className={styles.heroShield}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z"/>
                    <path d="m9 12 2 2 4-4"/>
                  </svg>
                </div>
                <span className={styles.heroLogoName}>Policyora</span>
              </div>
              <h2 className={styles.heroLeftTitle}>
                Policy Intelligence at <span className={styles.heroLeftAccent}>Scale</span>
              </h2>
              <p className={styles.heroLeftDesc}>
                Track every regulation, hearing, and bill — filtered, summarized, and ready for briefing.
              </p>
              <div className={styles.heroFeaturesList}>
                {heroFeatures.map(f => (
                  <div key={f.label} className={styles.heroFeatureItem}>
                    <div className={styles.heroFeatureIconWrap}>{f.icon}</div>
                    {f.label}
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.heroCircle1}/>
            <div className={styles.heroCircle2}/>
            {/* Wavy right edge */}
            <svg className={styles.heroWave} viewBox="0 0 60 300" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M30,0 C0,50 60,100 30,150 C0,200 60,250 30,300 L60,300 L60,0 Z" />
            </svg>
          </div>

          {/* ── Right content panel ── */}
          <div className={styles.heroRight}>
            <span className={styles.heroBadge}>AI Workspace for Policy &amp; Compliance Teams</span>
            <h1 className={styles.heroTitle}>
              Track and Understand Policy Changes Across Jurisdictions in{' '}
              <span className={styles.heroTitleAccent}>Real Time</span>
            </h1>
            <p className={styles.heroSubheading}>Built for policy, legal, and compliance teams who need faster, clearer insights.</p>
            <div className={styles.heroPillRow}>
              {[
                { label: 'Real-time Monitoring' },
                { label: 'AI Summaries' },
                { label: 'Global Coverage' },
                { label: 'Policy Chat' },
                { label: 'Smart Alerts' },
                { label: 'Trend Analytics' },
              ].map(p => (
                <span key={p.label} className={styles.heroPill}>{p.label}</span>
              ))}
            </div>
            <div className={styles.heroActions}>
              <Link className={styles.primaryButton} to="/signup">Start Free Trial</Link>
              <Link className={styles.secondaryButton} to="/login">See It in Action</Link>
            </div>
            <div className={styles.heroHighlightCluster}>
              <div className={`${styles.heroHighlight} ${styles.scrollAnimated} ${styles.scaleIn} ${styles.stagger1}`}>
                <span data-target={liveStats ? String(liveStats.regulations) : '0'}>
                  {liveStats ? liveStats.regulations.toLocaleString() : '—'}
                </span>
                <p>Policies &amp; regulations tracked</p>
              </div>
              <div className={`${styles.heroHighlight} ${styles.scrollAnimated} ${styles.scaleIn} ${styles.stagger2}`}>
                <span data-target={liveStats ? String(liveStats.jurisdictions) : '0'}>
                  {liveStats ? liveStats.jurisdictions.toLocaleString() : '—'}
                </span>
                <p>Jurisdictions covered</p>
              </div>
              <div className={`${styles.heroHighlight} ${styles.scrollAnimated} ${styles.scaleIn} ${styles.stagger3}`}>
                <span data-target={liveStats ? String(liveStats.countries) : '0'}>
                  {liveStats ? liveStats.countries.toLocaleString() : '—'}
                </span>
                <p>Countries represented</p>
              </div>
            </div>
            <div className={styles.heroTrustRow}>
              <div className={styles.heroAvatarStack}>
                <div className={styles.heroAvatar} style={{background:'#101828'}}>P</div>
                <div className={styles.heroAvatar} style={{background:'#475467'}}>L</div>
                <div className={styles.heroAvatar} style={{background:'#667085'}}>C</div>
                <div className={styles.heroAvatar} style={{background:'#444ce7'}}>A</div>
              </div>
              <span className={styles.heroTrustText}>Built for <strong>policy, legal &amp; compliance</strong> teams</span>
            </div>
          </div>
        </section>

        <section id="how-it-works" className={`${styles.howItWorksSection} ${styles.scrollAnimated} ${styles.fadeInUp}`}>
          <p className={styles.eyebrow}>How It Works</p>
          <h2 className={styles.sectionTitle}>From raw policy data to clear insights in 3 steps</h2>
          <div className={styles.stepsGrid}>
            <div className={`${styles.stepCard} ${styles.scrollAnimated} ${styles.scaleIn} ${styles.stagger1}`}>
              <div className={styles.stepIconWrap}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="2"/><path d="M4.93 4.93a10 10 0 0 0 0 14.14M19.07 4.93a10 10 0 0 1 0 14.14M7.76 7.76a6 6 0 0 0 0 8.49M16.24 7.76a6 6 0 0 1 0 8.49"/>
                </svg>
              </div>
              <div className={styles.stepNum}>01</div>
              <h3>Track Regulations</h3>
              <p>Monitor federal, state, and local policy sources in real time across all jurisdictions.</p>
            </div>
            <div className={`${styles.stepCard} ${styles.scrollAnimated} ${styles.scaleIn} ${styles.stagger2}`}>
              <div className={styles.stepIconWrap}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/>
                </svg>
              </div>
              <div className={styles.stepNum}>02</div>
              <h3>AI Summarizes Changes</h3>
              <p>Our AI instantly distills complex bills, hearings, and regulations into clear, actionable summaries.</p>
            </div>
            <div className={`${styles.stepCard} ${styles.scrollAnimated} ${styles.scaleIn} ${styles.stagger3}`}>
              <div className={styles.stepIconWrap}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/>
                </svg>
              </div>
              <div className={styles.stepNum}>03</div>
              <h3>Compare Across Jurisdictions</h3>
              <p>Benchmark policy landscapes side by side to identify trends, gaps, and strategic opportunities.</p>
            </div>
          </div>
        </section>

        <section className={styles.dashboardPreviewSection}>
          <div className={`${styles.dashboardPreviewContent} ${styles.scrollAnimated} ${styles.fadeInLeft}`}>
            <p className={styles.eyebrow}>Dashboard in Action</p>
            <h2>Everything your team needs in <span className={styles.headlineAccent}>one</span> workspace</h2>
            <p>
              From real-time regulation tracking to AI-generated summaries and a live global map —
              Policyora brings every signal into a single, powerful dashboard.
            </p>
            <ul className={styles.featureCheckList}>
              <li>Interactive key-dates timeline keeps high-impact bills on your radar.</li>
              <li>Jurisdiction filters instantly focus on the regions and agencies you cover.</li>
              <li>Summary cards transform dense artifacts into digestible, shareable updates.</li>
              <li>Live global map shows policy activity across every covered country.</li>
            </ul>
          </div>
          <div className={`${styles.slideshowWrapper} ${styles.scrollAnimated} ${styles.fadeInRight}`}>
            <div className={styles.slideshowFrame}>
              <div
                className={styles.slideshowTrack}
                style={{ transform: `translateX(-${slideIndex * 100}%)` }}
              >
                {dashboardSlides.map((slide, i) => (
                  <div key={i} className={styles.slide}>
                    <img src={slide.src} alt={slide.label} className={styles.slideImg} />
                  </div>
                ))}
              </div>
              <div className={styles.slideCaption}>{dashboardSlides[slideIndex].label}</div>
              <div className={styles.slideDots}>
                {dashboardSlides.map((_, i) => (
                  <button
                    key={i}
                    className={`${styles.slideDot} ${i === slideIndex ? styles.slideDotActive : ''}`}
                    onClick={() => setSlideIndex(i)}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className={styles.chatShowcaseSection}>
          <div className={`${styles.chatVideoWrapper} ${styles.scrollAnimated} ${styles.fadeInLeft}`}>
            <div className={styles.slideshowFrame}>
              <div
                className={styles.slideshowTrack}
                style={{ transform: `translateX(-${chatSlideIndex * 100}%)` }}
              >
                {chatSlides.map((slide, i) => (
                  <div key={i} className={styles.slide}>
                    <img src={slide.src} alt={slide.label} className={styles.slideImg} />
                  </div>
                ))}
              </div>
              <div className={styles.slideCaption}>{chatSlides[chatSlideIndex].label}</div>
              <div className={styles.slideDots}>
                {chatSlides.map((_, i) => (
                  <button
                    key={i}
                    className={`${styles.slideDot} ${i === chatSlideIndex ? styles.slideDotActive : ''}`}
                    onClick={() => setChatSlideIndex(i)}
                    aria-label={`Chat slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className={`${styles.chatTextContent} ${styles.scrollAnimated} ${styles.fadeInRight}`}>
            <p className={styles.eyebrow}>AI Policy Assistant</p>
            <h2>Chat with your policy data like <span className={styles.headlineAccent}>never</span> before</h2>
            <p>
              Our AI-powered chat assistant understands policy language and context. Ask questions about regulations, 
              jurisdictions, or compliance requirements and get instant, accurate answers with source citations.
            </p>
            <ul className={styles.chatList}>
              <li>Natural conversation flow - just ask questions like you would to a policy expert</li>
              <li>Provides detailed analysis with specific policy references and legal citations</li>
              <li>Context-aware responses that build on previous questions in the conversation</li>
              <li>Always includes source links so you can verify the information and dive deeper</li>
            </ul>
          </div>
        </section>

        <section className={styles.searchShowcaseSection}>
          <div className={`${styles.searchText} ${styles.scrollAnimated} ${styles.fadeInLeft}`}>
            <p className={styles.eyebrow}>Smart Search</p>
            <div className={styles.headingWithBadge}>
              <h2>Find <span className={styles.headlineAccent}>exactly</span> what you need across thousands of policies</h2>
            </div>
            <p>
              Our AI-powered search understands policy language, context, and intent. Filter by jurisdiction, 
              policy area, or date range to pinpoint the regulations that matter to your work.
            </p>
            <ul className={styles.searchFeatureList}>
              <li>Natural language search understands policy terminology and context</li>
              <li>Advanced filters narrow down by jurisdiction, policy area, and timeframes</li>
              <li>AI-generated summaries give you instant insights without reading full documents</li>
              <li>Real-time results as you type with smart suggestions and auto-complete</li>
            </ul>
          </div>
          <div className={`${styles.searchDemoWrapper} ${styles.scrollAnimated} ${styles.fadeInRight}`}>
            <div className={styles.slideshowFrame}>
              <div
                className={styles.slideshowTrack}
                style={{ transform: `translateX(-${searchSlideIndex * 100}%)` }}
              >
                {searchSlides.map((slide, i) => (
                  <div key={i} className={styles.slide}>
                    <img src={slide.src} alt={slide.label} className={styles.slideImg} />
                  </div>
                ))}
              </div>
              <div className={styles.slideCaption}>{searchSlides[searchSlideIndex].label}</div>
              <div className={styles.slideDots}>
                {searchSlides.map((_, i) => (
                  <button
                    key={i}
                    className={`${styles.slideDot} ${i === searchSlideIndex ? styles.slideDotActive : ''}`}
                    onClick={() => setSearchSlideIndex(i)}
                    aria-label={`Search slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Removed fabricated "Trusted by policy teams" partner logos and the
            invented testimonials — they named real third-party firms/competitors
            without permission (misleading + trademark risk). Re-add only real,
            permissioned customer logos / quotes. */}

      </main>

      <LegalModal
        open={legalModalOpen}
        documentType={legalDocumentType}
        onSelectDocument={setLegalDocumentType}
        onClose={() => setLegalModalOpen(false)}
      />

      <footer className={styles.footer}>
        <div className={styles.footerTop}>
          <div className={styles.footerBrand}>
            <div className={styles.footerLogoRow}>
              <img src="/Policy Ora Final Logo.png" alt="Policyora" className={styles.footerLogoImg} />
              <span className={styles.footerLogoName}>Policyora</span>
            </div>
            <p className={styles.footerTagline}>Policy intelligence for teams who can't afford to miss a change.</p>
            <a href="mailto:hello@policyora.ai" className={styles.footerEmail}>hello@policyora.ai</a>
          </div>
          <div className={styles.footerLinks}>
            <div className={styles.footerColumn}>
              <p className={styles.footerGroupLabel}>Product</p>
              <Link to="/pricing">Pricing</Link>
              <Link to="/faq">FAQs</Link>
            </div>
            <div className={styles.footerColumn}>
              <p className={styles.footerGroupLabel}>Company</p>
              <a href="mailto:hello@policyora.ai">Contact</a>
              <a href="https://www.linkedin.com/company/policyora" target="_blank" rel="noreferrer">LinkedIn</a>
            </div>
            <div className={styles.footerColumn}>
              <p className={styles.footerGroupLabel}>Legal</p>
              <button type="button" className={styles.footerLinkButton} onClick={() => openLegalModal('privacy')}>Privacy Policy</button>
              <button type="button" className={styles.footerLinkButton} onClick={() => openLegalModal('terms')}>Terms &amp; Conditions</button>
            </div>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <span>© {new Date().getFullYear()} Policyora · Knowledge Networks Inc.</span>
          <span className={styles.footerMadeWith}>Built for policy professionals</span>
        </div>
      </footer>
    </div>
  );
};
